/**
 * LES SIGNALEMENTS LUS POUR INFORMATION — module PUR (RQ, 05/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « À partir de mon dashboard, si j'ai lu une NC, je veux pouvoir la quitter.
 * Lecture de NC à but informatif et pédagogique. Comme ça je ne vois que les
 * nouvelles. Ajouter un badge new aux NC non quittées datant de moins d'une
 * semaine. »
 *
 * Puis, le même jour : « Nous avons opté pour que les NC soient une colonne
 * dans le dashboard. Y rajouter les réclamations dans le même flux. »
 *
 * ── UN SEUL FLUX, DEUX REGISTRES ──────────────────────────────────────────
 *
 * Une NC et une réclamation disent la même chose à qui lit son tableau de bord
 * le matin : « voici un écart récent, et ce qu'il change ». Elles se lisent
 * donc dans une seule colonne, triées par date, sans que la provenance
 * commande la place — seulement l'étiquette.
 *
 * ⚠️ `nature` N'EST PAS DÉCORATIVE : c'est elle qui dit quelle route appeler
 * pour quitter (`/ged/nc/:id/quitter` ou `/ged/recl/:id/quitter`), et quelles
 * rubriques se lisent sur la carte. Un signalement sans nature n'a pas de
 * geste possible.
 *
 * ⚠️ `quittes` EST UN ENSEMBLE DE `documentId`, SANS PRÉFIXE DE NATURE, et
 * c'est volontaire : un `documentId` Strapi est unique au-delà de sa
 * collection, deux registres n'en partagent jamais un. Les deux moitiés du
 * flux peuvent donc être lues par deux requêtes et fondues dans un seul
 * ensemble, sans clé composite à tenir de part et d'autre.
 *
 * ── ⚠️ CE QUE CE MÉCANISME N'EST PAS ──────────────────────────────────────
 *
 * Trois gestes portent sur un signalement et se ressemblent à l'écran. Les
 * confondre serait grave, dans les deux sens :
 *
 *   · la PRISE EN CHARGE — la lecture par le RQ ou son suppléant fait basculer
 *     le dossier vers l'analyse (décision du 26/08/2026,
 *     `POST /ged/nc/:id/lu`, `POST /ged/recl/:id/lu`). Quitter un signalement
 *     ici ne touche AUCUN statut ;
 *   · l'ACCUSÉ DE RÉCEPTION d'un document diffusé — une preuve opposable,
 *     relancée, comptée dans les indicateurs ISO. Quitter un signalement ne
 *     prouve RIEN et ne se relance pas ;
 *   · « MES NC À TRAITER » et « MES RÉCLAMATIONS À TRAITER » (tableau de bord,
 *     Ma vue) — une obligation. ⚠️ Elles ne sont PAS filtrées par ce
 *     mécanisme, et ne doivent jamais l'être : une tâche qu'on se cache reste
 *     due. Seule la colonne « Derniers signalements », qui est de la
 *     pédagogie, obéit à ce filtre.
 *
 * C'est donc un confort d'affichage PERSONNEL et RÉVERSIBLE. La demande le dit
 * elle-même : « à but informatif et pédagogique ».
 *
 * ── Pourquoi « nouveau » se calcule, et ne se stocke pas ──────────────────
 *
 * Un signalement est NOUVEAU pour moi si je ne l'ai pas quitté ET s'il a moins
 * d'une semaine. Le second terme dépend du jour où l'on regarde : stocker un
 * drapeau « nouveau » le figerait, et une NC resterait « new » six mois. On le
 * calcule donc à chaque affichage, et `aujourdhui` est TOUJOURS passé en
 * paramètre — jamais lu de l'horloge ici, sans quoi le module ne serait pas
 * pur et son test ne pourrait pas franchir la limite des sept jours.
 *
 * ⚠️ MODULE SANS AUCUN IMPORT (feuille) : il est compilé et éprouvé par
 * `npm test` (préfixe `front/`).
 */

/** La fenêtre de nouveauté, en jours. « Moins d'une semaine », donc 7. */
export const JOURS_NOUVEAUTE = 7;

/** Le registre d'où vient le signalement. Liste FERMÉE : un troisième registre
 *  devra s'ajouter ici, et dans la route qu'il faut appeler pour le quitter. */
export type NatureSignalement = 'nc' | 'recl';

export interface SignalementLisible {
  documentId: string;
  nature?: NatureSignalement;
  /** La date de DÉCLARATION, à défaut celle de détection — pour une NC, la
   *  même que celle que la liste affiche et sur laquelle elle trie. */
  dateDeclaration?: string | null;
  dateDetection?: string | null;
  /** La date de RÉCEPTION d'une réclamation : son seul repère de temps, et
   *  celui que son registre affiche. */
  dateReception?: string | null;
  /** La clé de qui a RÉDIGÉ le signalement (`detectePar` d'une NC). Vide quand
   *  le registre ne la tient pas — c'est le cas des réclamations. */
  auteur?: string | null;
}

/** La date qui datte le signalement pour le lecteur : celle qu'il voit à
 *  l'écran. Vide si aucune n'est lisible — et un signalement sans date n'est
 *  JAMAIS annoncé nouveau : affirmer « new » sans date serait deviner.
 *
 *  ⚠️ L'ORDRE DES TROIS CANDIDATES EST CELUI DU SENS, pas de la commodité :
 *  déclaration, puis détection, puis réception. Les deux premières ne
 *  concernent que les NC, la troisième que les réclamations — aucun dossier
 *  n'en porte deux, l'ordre ne tranche donc jamais d'ambiguïté réelle ; il
 *  évite seulement qu'un champ ajouté demain change la date affichée hier. */
export function dateDuSignalement(n: SignalementLisible): string {
  const d = String(n.dateDeclaration || n.dateDetection || n.dateReception || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
}

/** L'âge en jours entiers, par les CHAÎNES de date — pas d'objet `Date` sur un
 *  jour nu : le fuseau du navigateur ferait glisser un 1er au 31 du mois
 *  précédent, et un signalement déclaré aujourd'hui pourrait paraître vieux
 *  d'un jour. `-1` quand la date est illisible. */
export function ageEnJours(date: string, aujourdhui: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{4}-\d{2}-\d{2}$/.test(aujourdhui)) return -1;
  const jour = (s: string) => Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
  return Math.round((jour(aujourdhui) - jour(date)) / 86_400_000);
}

/**
 * Le signalement mérite-t-il le badge « nouveau » ?
 *
 * Trois conditions, et chacune a sa raison :
 *  · NON QUITTÉ — l'avoir lu est précisément ce qui le rend moins nouveau ;
 *  · MOINS DE SEPT JOURS — la demande ;
 *  · DATÉ — sans date, on ne sait pas, et on ne prétend pas savoir.
 *
 * ⚠️ Un signalement daté DANS LE FUTUR (âge négatif) est nouveau : une
 * déclaration saisie avec une date de détection du lendemain existe, et la
 * masquer du badge ferait disparaître la plus récente de toutes.
 */
export function estNouveau(n: SignalementLisible, quittes: ReadonlySet<string>, aujourdhui: string): boolean {
  if (quittes.has(n.documentId)) return false;
  const d = dateDuSignalement(n);
  if (!d) return false;
  return ageEnJours(d, aujourdhui) < JOURS_NOUVEAUTE;
}

/**
 * L'ensemble des signalements LUS pour moi : ceux que j'ai quittés, plus ceux
 * que j'ai RÉDIGÉS moi-même (RQ, 19/10/2026 : « quand je suis moi-même le
 * rédacteur du signalement, le considérer déjà comme lu et pas comme un
 * nouveau signalement »). On ne m'apprend rien en me montrant ce que je viens
 * d'écrire.
 *
 * ⚠️ CALCULÉ, JAMAIS ÉCRIT côté serveur : la règle tient aux signalements
 * passés comme aux futurs, et une vue fabriquée en base se confondrait avec un
 * geste réel de lecture. Sans `moi` (session pas lue), rien n'est ajouté.
 */
export function lusPourMoi(liste: SignalementLisible[], quittes: ReadonlySet<string>, moi: string): Set<string> {
  const lus = new Set(quittes);
  if (moi) for (const n of liste) if (n.auteur && n.auteur === moi) lus.add(n.documentId);
  return lus;
}

/** Ceux que je n'ai pas quittés, dans l'ordre reçu. */
export function nonQuittes<T extends SignalementLisible>(liste: T[], quittes: ReadonlySet<string>): T[] {
  return liste.filter((n) => !quittes.has(n.documentId));
}

/**
 * Fond les deux registres en UN flux, du plus récent au plus ancien.
 *
 * ⚠️ LE TRI SE FAIT SUR LA DATE AFFICHÉE, celle que `dateDuSignalement` rend
 * — et non sur un champ propre à chaque registre. Trier les NC sur la
 * déclaration et les réclamations sur la réception aurait donné une colonne
 * dont l'ordre ne correspond à aucune des dates qu'elle montre.
 *
 * ⚠️ LES SANS-DATE PASSENT À LA FIN, jamais en tête. Une chaîne vide compare
 * avant tout le reste : laissée telle quelle, elle aurait propulsé un dossier
 * dont on ne sait rien au sommet de « derniers signalements ».
 *
 * À date égale, l'ordre est STABLE (`sort` l'est) : l'ordre reçu décide, et
 * deux rendus consécutifs ne réarrangent pas la colonne sous les yeux.
 */
export function fondre<T extends SignalementLisible>(...registres: T[][]): T[] {
  const tous = registres.flat();
  return tous.sort((a, b) => {
    const da = dateDuSignalement(a); const db = dateDuSignalement(b);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return db.localeCompare(da);
  });
}

/**
 * Ce que la colonne « Derniers signalements » affiche.
 *
 * ⚠️ ELLE REND AUSSI `quittesMasques`, et ce n'est pas un détail : sans ce
 * compte, une colonne vide parce qu'on a tout lu se lirait « aucun écart
 * enregistré » — une AFFIRMATION fausse, et le piège des faux vides. L'écran
 * doit pouvoir dire « vous les avez tous lus » et proposer de les revoir.
 */
export function pourLeTableauDeBord<T extends SignalementLisible>(
  liste: T[], quittes: ReadonlySet<string>, aujourdhui: string, combien: number,
): { affiches: T[]; nouveaux: Set<string>; quittesMasques: number; total: number } {
  const restants = nonQuittes(liste, quittes);
  return {
    affiches: restants.slice(0, Math.max(0, combien)),
    nouveaux: new Set(
      liste.filter((n) => estNouveau(n, quittes, aujourdhui)).map((n) => n.documentId),
    ),
    quittesMasques: liste.length - restants.length,
    total: liste.length,
  };
}
